/* Shared catalogue facts for product pages and comparison. Never infer claims from titles or marketing copy. */
(() => {
  'use strict';
  if (window.DutProductFacts) return;

  const definitions = [
    ['material', 'Material', ['material', 'materials', 'product material']],
    ['body_material', 'Body material', ['body material']],
    ['head_material', 'Head material', ['head material']],
    ['dimensions', 'Dimensions', ['dimensions', 'product dimensions', 'overall dimensions']],
    ['length', 'Overall length', ['length', 'total length', 'overall length', 'product length']],
    ['insertable_length', 'Insertable length', ['insertable length']],
    ['diameter', 'Diameter', ['diameter', 'product diameter']],
    ['circumference', 'Circumference', ['circumference', 'girth']],
    ['width', 'Width', ['width', 'product width']],
    ['height', 'Height', ['height', 'product height']],
    ['weight', 'Weight', ['weight', 'product weight']],
    ['waterproof', 'Waterproof', ['waterproof', 'waterproof rating']],
    ['water_resistance', 'Water resistance', ['water resistance', 'water resistant', 'water resistance rating']],
    ['splashproof', 'Splashproof', ['splashproof', 'splash proof']],
    ['power', 'Power source', ['power', 'power source', 'power supply']],
    ['battery', 'Battery', ['battery', 'batteries', 'battery type', 'batteries required']],
    ['recharge_time', 'Charging time', ['recharge time', 'charging time', 'charge time']],
    ['battery_life', 'Battery life', ['battery life', 'run time', 'runtime', 'running time', 'play time']],
    ['speeds', 'Speeds', ['speeds', 'vibration speeds', 'speed settings']],
    ['modes', 'Modes', ['modes', 'vibration modes', 'vibration patterns']],
    ['care', 'Care', ['care', 'care instructions', 'cleaning instructions']],
    ['lube_compatibility', 'Lubricant compatibility', ['lubricant compatibility', 'lube compatibility']],
    ['size', 'Size', ['size', 'product size']],
    ['colour', 'Colour', ['colour', 'color']],
  ];
  const clean = (value) => String(value || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
  const normaliseLabel = (value) => clean(value).toLowerCase().replace(/[–—-]/g, ' ').replace(/\s+/g, ' ').replace(/\s*:\s*$/, '');
  const aliases = new Map();
  definitions.forEach(([key, label, names]) => names.forEach((name) => aliases.set(name, { key, label })));
  const blockTags = new Set(['ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'P', 'SECTION', 'UL', 'OL']);
  const excludedScope = /\b(packag(?:e|ing)|shipping|remote(?: control)?|charger|charging cable|comparison|compare)\b/i;

  function extract(product) {
    if (!product || typeof product !== 'object') return [];
    const html = typeof product.description === 'string' ? product.description : product.body_html;
    if (typeof html !== 'string' || !html.trim()) return [];

    // A detached template is inert. Only text is read; catalogue HTML is never inserted into the results.
    const source = document.createElement('template');
    source.innerHTML = html;
    source.content.querySelectorAll('script,style,template,noscript,iframe,object,embed,svg,canvas,form,[hidden],[aria-hidden="true"]').forEach((node) => node.remove());
    const found = new Map();
    const conflicts = new Set();

    function add(rawLabel, rawValue) {
      const definition = aliases.get(normaliseLabel(rawLabel));
      const value = clean(rawValue);
      if (!definition || !value || value.length > 240 || /^(?:n\/?a|not (?:available|specified|provided)|unknown|[-–—])$/i.test(value)) return;
      if (conflicts.has(definition.key)) return;
      const previous = found.get(definition.key);
      if (previous && previous.value.toLowerCase() !== value.toLowerCase()) {
        found.delete(definition.key);
        conflicts.add(definition.key);
        return;
      }
      found.set(definition.key, { ...definition, value });
    }

    // Preserve explicit paragraph/list boundaries, including the catalogue's <br>- Label: value format.
    let text = '';
    let sectionExcluded = false;
    function walk(node) {
      if (node.nodeType === Node.TEXT_NODE) { text += node.textContent; return; }
      if (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;
      if (node.nodeName === 'BR') { text += '\n'; return; }
      // Two-column specification tables only. Keep packaging and remote-control sections separate.
      if (node.nodeName === 'TABLE') {
        const rows = [...node.querySelectorAll('tr')];
        const multiColumn = rows.some((row) => row.cells.length > 2 || [...row.cells].some((cell) => cell.colSpan > 1));
        if (!sectionExcluded && !multiColumn && !excludedScope.test(node.querySelector('caption')?.textContent || '')) {
          rows.forEach((row) => {
            if (row.cells.length === 2 && !row.querySelector('table')) add(row.cells[0].textContent, row.cells[1].textContent);
          });
        }
        text += '\n';
        return;
      }
      if (node.nodeName === 'DL') {
        if (!sectionExcluded) node.querySelectorAll('dt').forEach((term) => {
          const value = term.nextElementSibling;
          if (value?.tagName === 'DD') add(term.textContent, value.textContent);
        });
        text += '\n';
        return;
      }
      const block = blockTags.has(node.nodeName);
      if (block) text += '\n';
      if (/^H[1-6]$/.test(node.nodeName)) {
        sectionExcluded = excludedScope.test(node.textContent);
        text += sectionExcluded ? '\u001eEXCLUDE\n' : '\u001ePRODUCT\n';
      }
      node.childNodes.forEach(walk);
      if (block) text += '\n';
    }
    walk(source.content);
    let excluded = false;
    for (const rawLine of text.split('\n')) {
      if (rawLine === '\u001eEXCLUDE') { excluded = true; continue; }
      if (rawLine === '\u001ePRODUCT') { excluded = false; continue; }
      if (excluded) continue;
      const line = clean(rawLine).replace(/^[-–—•]\s*/, '');
      const colon = line.indexOf(':');
      if (colon < 1 || colon > 42 || !aliases.has(normaliseLabel(line.slice(0, colon)))) continue;

      // Commas can belong to a material blend or dimension; split only before another recognised label.
      const parts = line.split(/[,;]\s*(?=[a-z][a-z -]{1,40}:)/i);
      let label = line.slice(0, colon);
      let value = '';
      parts.forEach((part, index) => {
        const split = part.indexOf(':');
        const nextLabel = split > -1 ? part.slice(0, split) : '';
        if (index === 0) { value = part.slice(split + 1); return; }
        if (aliases.has(normaliseLabel(nextLabel))) {
          add(label, value);
          label = nextLabel;
          value = part.slice(split + 1);
        } else {
          value += ', ' + part;
        }
      });
      add(label, value);
    }
    return definitions.flatMap(([key]) => found.has(key) ? [found.get(key)] : []);
  }

  function mount(root = document) {
    root.querySelectorAll('[data-dut-product-facts]:not([data-facts-ready])').forEach((section) => {
      section.dataset.factsReady = '';
      const source = section.querySelector('template[data-dut-facts-source]');
      const list = section.querySelector('[data-dut-facts-list]');
      if (!source || !list) return;
      const allFacts = extract({ description: source.content.textContent });
      const priority = ['material', 'body_material', 'power', 'waterproof', 'water_resistance', 'splashproof', 'modes', 'speeds', 'care'];
      const facts = section.dataset.factsLimit ? allFacts.filter(fact => priority.includes(fact.key)).sort((a, b) => priority.indexOf(a.key) - priority.indexOf(b.key)).slice(0, Number(section.dataset.factsLimit)) : allFacts;
      if (!facts.length) return;
      const fragment = document.createDocumentFragment();
      facts.forEach(({ label, value }) => {
        const row = document.createElement('div');
        const term = document.createElement('dt');
        const detail = document.createElement('dd');
        term.textContent = label;
        detail.textContent = value;
        row.append(term, detail);
        fragment.append(row);
      });
      list.replaceChildren(fragment);
      section.hidden = false;
    });
  }

  window.DutProductFacts = Object.freeze({ extract });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
})();
